import Intercom from "@intercom/messenger-js-sdk";
import { useEffect } from "react";
import { User } from "../api/user";
import { getPreferredName } from "../redacted-display";

const INTERCOM_APP_ID = "ctpql9ea";

type IntercomPreset = "dashboard" | "login" | "onboarding";

const PRESETS: Record<
  IntercomPreset,
  {
    pageTitle: string;
    alignment: "left" | "right";
    horizontalPadding: number;
    hideDefaultLauncher: boolean;
    customAttributes?: Record<string, unknown>;
  }
> = {
  dashboard: {
    pageTitle: "Mentors",
    alignment: "left",
    horizontalPadding: 100,
    hideDefaultLauncher: true,
  },
  login: {
    pageTitle: "Login - Mentor Dashboard Access Request",
    alignment: "left",
    horizontalPadding: 100,
    hideDefaultLauncher: true,
    customAttributes: {
      access_request: true,
      page_context: "login_page",
      security_note:
        "User attempting to access mentor dashboard - identity verification required",
    },
  },
  onboarding: {
    pageTitle: "Mentor matching profile onboarding",
    alignment: "left",
    horizontalPadding: 24,
    hideDefaultLauncher: true,
    customAttributes: {
      page_context: "mentor_profile_onboarding",
    },
  },
};

export const useIntercom = (
  user?: User,
  preset: IntercomPreset = "dashboard",
) => {
  useEffect(() => {
    if (user?.metadata?.intercom_disabled) return;

    const config =
      PRESETS[!user && preset === "dashboard" ? "login" : preset];

    if (user) {
      Intercom({
        app_id: INTERCOM_APP_ID,
        page_title: config.pageTitle,
        alignment: config.alignment,
        horizontal_padding: config.horizontalPadding,
        hide_default_launcher: config.hideDefaultLauncher,
        user_id: user.id,
        name: getPreferredName(user) || "Mentor",
        email: user.email ?? undefined,
        created_at: new Date(user.created_at).getTime() / 1000,
        ...(config.customAttributes
          ? { custom_attributes: config.customAttributes }
          : {}),
      });
      return;
    }

    Intercom({
      app_id: INTERCOM_APP_ID,
      page_title: config.pageTitle,
      alignment: config.alignment,
      horizontal_padding: config.horizontalPadding,
      hide_default_launcher: config.hideDefaultLauncher,
      custom_attributes: config.customAttributes,
    });
  }, [user, preset]);

  const openMessenger = () => {
    if (window.Intercom) {
      window.Intercom("show");
    }
  };

  return { openMessenger };
};
